import ProviderIcon from 'features/common/ui/ProviderIcon/ProviderIcon'

import {
  BigQueryConfigType,
  PostgresConfigType,
  Provider,
  ProviderType,
  RedshiftConfigType,
  SnowflakeConfigType
} from './types'
import { ReactElement } from 'react'
import { DatabricksConfigType } from '.'

interface ProviderIdLabelIcon {
  id: ProviderType
  label: string
  icon: ReactElement
}

export const Providers: Record<string, Provider> = {
  bigquery: {
    label: 'BigQuery',
    labelLong: 'Google BigQuery',
    docs: 'https://docs.carto.com/carto-user-manual/connections/bigquery',
    icon: <ProviderIcon size="small" provider={ProviderType.BIGQUERY} />,
    hasSpatialExtension: true,
    config: {
      type: BigQueryConfigType.ServiceAccount,
      credentials: {
        billing_project: {
          label: 'connections.create.bigquery.form.billingProjectIdLabel',
          showInDetails: true
        },
        client_email: {
          label: 'connections.create.bigquery.form.clientEmailLabel',
          showInDetails: true
        },
        audience: {
          label: 'connections.create.bigquery.form.audienceLabel',
          showInDetails: true
        }
      }
    }
  },
  snowflake: {
    label: 'Snowflake',
    docs: 'https://docs.carto.com/carto-user-manual/connections/snowflake',
    icon: <ProviderIcon size="small" provider={ProviderType.SNOWFLAKE} />,
    hasSpatialExtension: false,
    config: {
      type: SnowflakeConfigType.SnowflakeAccount,
      credentials: {
        database: {
          label: 'connections.create.snowflake.form.databaseLabel',
          placeholder: 'connections.create.snowflake.form.databasePlaceholder',
          showInDetails: true
        },
        account: {
          label: 'connections.create.snowflake.form.accountLabel',
          placeholder: 'connections.create.snowflake.form.accountPlaceholder',
          showInDetails: true
        },
        username: {
          label: 'connections.create.snowflake.form.usernameLabel',
          placeholder: 'connections.create.snowflake.form.usernamePlaceholder'
        },
        password: {
          label: 'connections.create.snowflake.form.passwordLabel',
          placeholder: 'connections.create.snowflake.form.passwordPlaceholder',
          type: 'password',
          optionalOnEdit: true
        },
        warehouse: {
          label: 'connections.create.snowflake.form.warehouseLabel',
          placeholder: 'connections.create.snowflake.form.warehousePlaceholder',
          optional: true
        }
      }
    }
  },
  postgres: {
    label: 'PostgreSQL',
    docs: 'https://docs.carto.com/carto-user-manual/connections/postgresql',
    hasSpatialExtension: false,
    icon: <ProviderIcon size="small" provider={ProviderType.POSTGRES} />,
    config: {
      type: PostgresConfigType.UserPassword,
      credentials: {
        database: {
          label: 'connections.create.postgres.form.databaseLabel',
          placeholder: 'connections.create.postgres.form.databasePlaceholder',
          showInDetails: true
        },
        host: {
          label: 'connections.create.postgres.form.serverLabel',
          placeholder: 'connections.create.postgres.form.serverPlaceholder',
          showInDetails: true
        },
        port: {
          label: 'connections.create.postgres.form.portLabel',
          placeholder: 'connections.create.postgres.form.portPlaceholder',
          type: 'number',
          defaultValue: 5432
        },
        user: {
          label: 'connections.create.postgres.form.usernameLabel',
          placeholder: 'connections.create.postgres.form.usernamePlaceholder'
        },
        password: {
          label: 'connections.create.postgres.form.passwordLabel',
          placeholder: 'connections.create.postgres.form.passwordPlaceholder',
          type: 'password',
          optionalOnEdit: true
        }
      }
    }
  },
  redshift: {
    label: 'Redshift',
    labelLong: 'AWS Redshift',
    docs: 'https://docs.carto.com/carto-user-manual/connections/redshift',
    hasSpatialExtension: false,
    icon: <ProviderIcon size="small" provider={ProviderType.REDSHIFT} />,
    config: {
      type: RedshiftConfigType.UserPassword,
      credentials: {
        database: {
          label: 'connections.create.redshift.form.databaseLabel',
          placeholder: 'connections.create.redshift.form.databasePlaceholder',
          showInDetails: true
        },
        host: {
          label: 'connections.create.redshift.form.clusterLabel',
          placeholder: 'connections.create.redshift.form.clusterPlaceholder',
          showInDetails: true
        },
        port: {
          label: 'connections.create.redshift.form.portLabel',
          placeholder: 'connections.create.redshift.form.portPlaceholder',
          type: 'number',
          defaultValue: 5439
        },
        user: {
          label: 'connections.create.redshift.form.usernameLabel',
          placeholder: 'connections.create.redshift.form.usernamePlaceholder'
        },
        password: {
          label: 'connections.create.redshift.form.passwordLabel',
          placeholder: 'connections.create.redshift.form.passwordPlaceholder',
          type: 'password',
          optionalOnEdit: true
        }
      }
    }
  },
  carto: {
    label: 'CARTO',
    labelLong: 'CARTO Data Warehouse',
    hasSpatialExtension: true,
    icon: <ProviderIcon size="small" provider={ProviderType.CARTO} />,
    config: {
      type: ''
    }
  },
  carto_dw: {
    label: 'CARTO',
    labelLong: 'CARTO Data Warehouse',
    hasSpatialExtension: true,
    icon: <ProviderIcon size="small" provider={ProviderType.CARTO_DW} />,
    config: {
      type: ''
    }
  },
  databricks: {
    label: 'Databricks',
    docs: 'https://docs.carto.com/carto-user-manual/connections/databricks',
    hasSpatialExtension: false,
    icon: <ProviderIcon size="small" provider={ProviderType.DATABRICKS} />,
    config: {
      type: DatabricksConfigType.SparkOdbc,
      credentials: {
        database: {
          label: 'connections.create.databricks.form.databaseLabel',
          placeholder: 'connections.create.databricks.form.databasePlaceholder',
          showInDetails: true
        },
        server: {
          label: 'connections.create.databricks.form.serverLabel',
          placeholder: 'connections.create.databricks.form.serverPlaceholder',
          showInDetails: true
        },
        port: {
          label: 'connections.create.databricks.form.portLabel',
          placeholder: 'connections.create.databricks.form.portPlaceholder',
          type: 'number',
          defaultValue: 443
        },
        httpPath: {
          label: 'connections.create.databricks.form.httpPathLabel',
          placeholder: 'connections.create.databricks.form.httpPathLabel'
        },
        password: {
          label: 'connections.create.databricks.form.passwordLabel',
          placeholder: 'connections.create.databricks.form.passwordPlaceholder',
          type: 'password',
          optionalOnEdit: true
        }
      }
    }
  },
  databricksRest: {
    label: 'Databricks',
    docs: 'https://docs.carto.com/carto-user-manual/connections/databricks',
    hasSpatialExtension: false,
    icon: <ProviderIcon size="small" provider={ProviderType.DATABRICKS} />,
    config: {
      type: DatabricksConfigType.Rest,
      credentials: {
        connection_type: {
          label: 'connections.create.databricksRest.form.connectionTypeLabel',
          showInDetails: true
        },
        catalog: {
          label: 'connections.create.databricksRest.form.catalogLabel',
          placeholder: 'connections.create.databricksRest.form.catalogPlaceholder',
          showInDetails: true
        },
        clusterId: {
          label: 'connections.create.databricksRest.form.clusterIdLabel',
          showInDetails: false
        },
        host: {
          label: 'connections.create.databricksRest.form.hostLabel',
          placeholder: 'connections.create.databricks.form.hostPlaceholder',
          showInDetails: true
        },
        email: {
          label: 'connections.create.databricksRest.form.emailLabel',
          showInDetails: true
        },
        clientId: {
          label: 'connections.create.databricksRest.form.servicePrincipalClientIdLabel',
          showInDetails: true
        },
        token: {
          label: 'connections.create.databricksRest.form.tokenLabel',
          placeholder: 'connections.create.databricksRest.form.tokenPlaceholder',
          type: 'password',
          optionalOnEdit: true
        },
        warehouseId: {
          label: 'connections.create.databricksRest.form.warehouseLabel',
          showInDetails: true
        }
      }
    }
  }
}

const sortAlphabetically = (prov1: ProviderIdLabelIcon, prov2: ProviderIdLabelIcon) => {
  if (prov1.label < prov2.label) return -1
  if (prov1.label > prov2.label) return 1
  return 0
}

export function getProviders(sorted = false): ProviderIdLabelIcon[] {
  const providers = Object.entries(Providers)
    .filter(([id]) => id !== ProviderType.CARTO)
    .map(([id, { label, icon }]) => ({ id: id as ProviderType, label, icon }))
  if (sorted) providers.sort(sortAlphabetically)

  return providers
}

export function getProvider(id: string | undefined): Provider {
  if (!id || !Providers[id]) throw new Error(`Invalid provider ${id}`)
  return Providers[id]
}
